import React, { useEffect, useRef } from 'react';
import Menu from 'storybookConfig/stories/cells/Menu';
import { type RootState, useAppDispatch, useAppSelector } from 'store';
import Categories from 'components/Dashboard/LeftMenu/Categories';
import { closeCategories, openCategories } from 'store/slices/sidebar';
import Body from 'storybookConfig/stories/molecules/Body';
import Collapse, { CollapseChevron } from 'storybookConfig/stories/molecules/Collapse';
import { SubLinkContainer } from './Shared.styles';
import useLoadCategories, { type CategoryNode } from 'hooks/useLoadCategories';

interface MarketplaceLinksProps {
  isActive: (path: string) => boolean;
}

const MarketplaceLinks = ({ isActive }: MarketplaceLinksProps) => {
  const [expanded, setExpanded] = React.useState(true);
  const [categoriesExpanded, setCategoriesExpanded] = React.useState(false);
  const marketplaceId = React.useId();
  const categoriesId = React.useId();
  const dispatch = useAppDispatch();
  const categoriesRef = useRef<HTMLDivElement | null>(null);
  const { categoriesOpen } = useAppSelector((state: RootState) => state.sidebar);
  const { categories }: { categories: CategoryNode } = useLoadCategories();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        dispatch(closeCategories());
      }
    };

    if (categoriesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [categoriesOpen, dispatch]);

  return (
    <>
      <Menu.LinkButton
        aria-expanded={expanded}
        aria-controls={marketplaceId}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <Body variant="Body/Header">Marketplace</Body>
        <CollapseChevron open={expanded} />
      </Menu.LinkButton>
      <Collapse id={marketplaceId} open={expanded}>
        <SubLinkContainer>
          <Menu.Link
            $isActive={isActive('/marketplace/home/featured')}
            $iconName="house"
            to="/marketplace/home/featured"
          >
            Home
          </Menu.Link>
          <Menu.LinkButton
            $iconName="layout-grid"
            $isActive={isActive('/marketplace/category/:id')}
            aria-expanded={categoriesExpanded}
            aria-controls={categoriesId}
            onClick={() => {
              setCategoriesExpanded(!categoriesExpanded);
            }}
          >
            Categories
            <CollapseChevron open={categoriesExpanded} />
          </Menu.LinkButton>
          <Collapse id={categoriesId} open={categoriesExpanded}>
            <SubLinkContainer customMarginLeft="24px" borderLeft>
              <Menu.Link to="/marketplace/category/0">All</Menu.Link>
              <Menu.Link to="/marketplace/category/1">Animals & Pets</Menu.Link>
              <Menu.Link to="/marketplace/category/166">Apparel</Menu.Link>
              <Menu.LinkButton
                onClick={() => {
                  dispatch(openCategories());
                }}
              >
                See More
              </Menu.LinkButton>
            </SubLinkContainer>
          </Collapse>
          <Menu.Link
            $isActive={isActive('/marketplace/home/favorites')}
            $iconName="heart"
            to="/marketplace/home/favorites"
          >
            Favorites
          </Menu.Link>
          <Menu.Link
            $isActive={isActive('/marketplace/home/recommendations')}
            $iconName="file-plus"
            to="/marketplace/home/recommendations"
          >
            Recommendations
          </Menu.Link>
        </SubLinkContainer>
      </Collapse>

      {categoriesOpen && (
        <div ref={categoriesRef}>
          <Categories data={categories} />
        </div>
      )}
    </>
  );
};

export default MarketplaceLinks;
